import { useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  ShieldCheck, Heart, ChartLineUp, ArrowRight, CheckCircle,
  WarningCircle, CurrencyDollar, Clock, Lock, TrendUp, Scales, Vault, Bank, Repeat,
  Briefcase, Flag, ChartLine
} from "@phosphor-icons/react";
import Retire401k from "@/pages/Retire401k";
import RetireTSP from "@/pages/RetireTSP";
import RetireIndexFunds from "@/pages/RetireIndexFunds";
import RetireCompare from "@/pages/RetireCompare";

/* ============================================================
   WHOLE LIFE INSURANCE
   ============================================================ */
function WholeLifeTab() {
  return (
    <div className="p-5 max-w-[1400px] space-y-6" data-testid="whole-life-tab">
      {/* Hero */}
      <div className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-sm bg-blue-900/20 flex items-center justify-center">
            <ShieldCheck size={20} weight="bold" className="text-blue-400" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-zinc-50">Whole Life Insurance</h2>
            <p className="text-xs text-zinc-400">Permanent coverage with guaranteed cash value growth</p>
          </div>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed">
          Whole life insurance is the simplest type of permanent life insurance. You pay a fixed premium every month for your entire life, and in return you get two things: a guaranteed death benefit paid to your family when you die, and a cash value account that grows slowly over time. Think of it as a savings account bundled with life insurance — except the growth rate is low but guaranteed.
        </p>
      </div>

      {/* How it works */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm overflow-hidden">
        <div className="p-5 border-b border-zinc-800/50">
          <h3 className="text-sm font-heading font-bold text-zinc-100 flex items-center gap-2">
            <Clock size={14} weight="bold" className="text-blue-400" /> How Whole Life Works
          </h3>
        </div>
        <div className="p-5 space-y-3">
          {[
            "You pay the same premium every month from the day you start until the day you die. The amount never changes.",
            "Part of your premium goes toward the death benefit (the payout your family receives). Part goes into a cash value account.",
            "The cash value grows at a guaranteed rate set by the insurance company — typically around 1% to 3% per year.",
            "After several years, your cash value builds up and you can borrow against it or withdraw from it while you're still alive.",
            "When you die, your beneficiaries receive the death benefit. The cash value does NOT get paid out on top — it's absorbed back into the policy.",
          ].map((step, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-blue-900/30 text-blue-400 flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i + 1}</span>
              <p className="text-xs text-zinc-300 leading-relaxed">{step}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Pros and Cons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-zinc-900/40 border border-emerald-900/30 rounded-sm p-5">
          <h3 className="text-[10px] uppercase tracking-wider text-emerald-400 mb-3 font-medium flex items-center gap-1.5">
            <CheckCircle size={12} weight="bold" /> Advantages
          </h3>
          <ul className="space-y-2.5">
            {[
              "Guaranteed death benefit — Your family is covered no matter when you die, as long as you pay premiums.",
              "Cash value is guaranteed — It grows slowly but it will never go down, even if the stock market crashes.",
              "Fixed premiums — You'll never pay more than the day you signed up. Easy to budget.",
              "Tax-deferred growth — You don't pay taxes on the cash value as it grows.",
              "You can borrow against it — Need cash? You can take a policy loan without a credit check.",
              "Dividends — Some policies from mutual insurance companies pay annual dividends (not guaranteed but common).",
            ].map((pro, i) => (
              <li key={i} className="flex items-start gap-2">
                <ArrowRight size={10} weight="bold" className="text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-xs text-zinc-300 leading-relaxed">{pro}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-zinc-900/40 border border-red-900/30 rounded-sm p-5">
          <h3 className="text-[10px] uppercase tracking-wider text-red-400 mb-3 font-medium flex items-center gap-1.5">
            <WarningCircle size={12} weight="bold" /> Disadvantages
          </h3>
          <ul className="space-y-2.5">
            {[
              "Expensive — Whole life costs 5x to 15x more than term life insurance for the same death benefit.",
              "Low returns — The guaranteed 1-3% growth rate barely keeps up with inflation. You'd earn more in a basic index fund.",
              "Slow cash value buildup — It takes 10-15 years before your cash value becomes meaningful.",
              "Surrender charges — If you cancel early (first 10-15 years), you'll lose a significant portion of what you paid.",
              "Complexity — The policy structure can be confusing, and agents are incentivized to sell it because their commissions are high.",
              "Death benefit minus cash value — Your family only gets the death benefit, not the death benefit plus cash value.",
            ].map((con, i) => (
              <li key={i} className="flex items-start gap-2">
                <ArrowRight size={10} weight="bold" className="text-red-400 flex-shrink-0 mt-0.5" />
                <span className="text-xs text-zinc-300 leading-relaxed">{con}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Key numbers */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-5">
        <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3 font-medium">Typical Numbers</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Monthly Cost (Healthy 30yr, $500K)", value: "$300 – $600", color: "text-zinc-200" },
            { label: "Cash Value Growth Rate", value: "1% – 3%/yr", color: "text-blue-400" },
            { label: "Years to Break Even", value: "12 – 20 years", color: "text-amber-400" },
            { label: "Coverage Duration", value: "Lifetime", color: "text-emerald-400" },
          ].map((stat, i) => (
            <div key={i}>
              <p className="text-[9px] uppercase tracking-wider text-zinc-600 mb-1">{stat.label}</p>
              <p className={`text-sm font-data font-bold ${stat.color}`}>{stat.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Who it's for */}
      <div className="bg-zinc-900/30 border border-zinc-800/50 rounded-sm p-5">
        <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3 font-medium">Who Should Consider Whole Life?</h3>
        <ul className="space-y-2">
          {[
            "People who want guaranteed, permanent coverage that never expires.",
            "High-income earners who have already maxed out their 401(k), IRA, and other tax-advantaged accounts.",
            "Estate planning — wealthy individuals use it to cover estate taxes so their heirs don't have to sell assets.",
            "Business owners who need a key-person policy or buy-sell agreement funding.",
            "People who value certainty over growth — they'd rather have a guaranteed 2% than risk the stock market.",
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2">
              <CheckCircle size={10} weight="bold" className="text-zinc-500 flex-shrink-0 mt-0.5" />
              <span className="text-xs text-zinc-400 leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ============================================================
   INDEXED UNIVERSAL LIFE (IUL)
   ============================================================ */
function IulTab() {
  return (
    <div className="p-5 max-w-[1400px] space-y-6" data-testid="iul-tab">
      {/* Hero */}
      <div className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-sm bg-violet-900/20 flex items-center justify-center">
            <TrendUp size={20} weight="bold" className="text-violet-400" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-zinc-50">Indexed Universal Life (IUL)</h2>
            <p className="text-xs text-zinc-400">Flexible permanent insurance with market-linked cash value growth</p>
          </div>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed">
          An Indexed Universal Life (IUL) policy is permanent life insurance where your cash value growth is tied to a stock market index (usually the S&P 500). You don't invest directly in the market — instead, the insurance company credits your account based on how the index performs, with a floor (you can't lose money) and a cap (there's a maximum you can earn). Think of it as life insurance with a chance to earn more than whole life, but with more moving parts.
        </p>
      </div>

      {/* How it works */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm overflow-hidden">
        <div className="p-5 border-b border-zinc-800/50">
          <h3 className="text-sm font-heading font-bold text-zinc-100 flex items-center gap-2">
            <ChartLineUp size={14} weight="bold" className="text-violet-400" /> How IULs Work
          </h3>
        </div>
        <div className="p-5 space-y-3">
          {[
            "You pay a premium, but unlike whole life, the amount is flexible. You can pay more or less (within limits) depending on your finances.",
            "The insurance company takes out the cost of insurance (death benefit cost) and fees. The rest goes into your cash value account.",
            "Your cash value is credited based on an index (like the S&P 500). If the index goes up 10% and your cap is 12%, you earn 10%. If the index goes up 15%, you only earn 12% (the cap).",
            "If the index goes down, you earn 0% — not negative. The floor (typically 0-1%) protects you from market losses. You never lose cash value due to market drops.",
            "You can adjust your death benefit up or down over time. You can also skip or reduce premiums if your cash value is large enough to cover the insurance costs.",
            "Like whole life, you can borrow against the cash value tax-free (if done correctly) or withdraw from it.",
          ].map((step, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-violet-900/30 text-violet-400 flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i + 1}</span>
              <p className="text-xs text-zinc-300 leading-relaxed">{step}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Cap, Floor, Participation Rate */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-5">
        <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-4 font-medium">The Three Key Numbers</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-zinc-950/50 border border-zinc-800/40 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendUp size={14} weight="bold" className="text-emerald-400" />
              <span className="text-xs font-medium text-zinc-200">Cap Rate</span>
            </div>
            <p className="text-lg font-data font-bold text-emerald-400 mb-1">8% – 13%</p>
            <p className="text-[10px] text-zinc-500 leading-relaxed">The maximum you can earn in a year, no matter how well the index does. If the S&P goes up 25%, you still only get the cap.</p>
          </div>
          <div className="bg-zinc-950/50 border border-zinc-800/40 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Lock size={14} weight="bold" className="text-blue-400" />
              <span className="text-xs font-medium text-zinc-200">Floor Rate</span>
            </div>
            <p className="text-lg font-data font-bold text-blue-400 mb-1">0% – 1%</p>
            <p className="text-[10px] text-zinc-500 leading-relaxed">The minimum you earn, even in a down year. If the S&P drops 30%, your cash value stays the same (earns 0%). You never go negative from market performance.</p>
          </div>
          <div className="bg-zinc-950/50 border border-zinc-800/40 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Scales size={14} weight="bold" className="text-amber-400" />
              <span className="text-xs font-medium text-zinc-200">Participation Rate</span>
            </div>
            <p className="text-lg font-data font-bold text-amber-400 mb-1">50% – 100%</p>
            <p className="text-[10px] text-zinc-500 leading-relaxed">The percentage of the index gain you actually receive. At 80% participation: if the S&P gains 10%, you earn 8%. Some policies offer 100%.</p>
          </div>
        </div>
      </div>

      {/* Pros and Cons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-zinc-900/40 border border-emerald-900/30 rounded-sm p-5">
          <h3 className="text-[10px] uppercase tracking-wider text-emerald-400 mb-3 font-medium flex items-center gap-1.5">
            <CheckCircle size={12} weight="bold" /> Advantages
          </h3>
          <ul className="space-y-2.5">
            {[
              "Higher growth potential — Can earn 5-8% in good years vs. whole life's 1-3%.",
              "Downside protection — The floor means you don't lose cash value when the market crashes.",
              "Flexible premiums — Pay more when you can afford it, less when times are tight.",
              "Tax-free income — Policy loans are not taxable income if structured properly. This is the #1 reason wealthy people use IULs.",
              "Adjustable death benefit — Increase or decrease coverage as your needs change.",
              "No direct market risk — You're linked to the index, not invested in it. The insurance company bears the risk.",
            ].map((pro, i) => (
              <li key={i} className="flex items-start gap-2">
                <ArrowRight size={10} weight="bold" className="text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-xs text-zinc-300 leading-relaxed">{pro}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-zinc-900/40 border border-red-900/30 rounded-sm p-5">
          <h3 className="text-[10px] uppercase tracking-wider text-red-400 mb-3 font-medium flex items-center gap-1.5">
            <WarningCircle size={12} weight="bold" /> Disadvantages
          </h3>
          <ul className="space-y-2.5">
            {[
              "Caps limit your upside — In a huge bull market year, you only earn up to the cap while index fund investors earn it all.",
              "Fees are high — Insurance charges, administrative fees, and rider costs eat into your returns. Total annual fees can be 2-3%.",
              "Complexity — IULs are the most complex life insurance product. Many buyers don't fully understand what they're getting.",
              "Illustrations can be misleading — Agents show best-case projections that assume max returns every year. Real results are often lower.",
              "Cost of insurance rises with age — As you get older, the insurance cost deducted from your cash value increases, which can drain it.",
              "If underfunded, the policy can lapse — If you don't put in enough money and the market underperforms, your policy can collapse.",
            ].map((con, i) => (
              <li key={i} className="flex items-start gap-2">
                <ArrowRight size={10} weight="bold" className="text-red-400 flex-shrink-0 mt-0.5" />
                <span className="text-xs text-zinc-300 leading-relaxed">{con}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Key numbers */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-5">
        <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3 font-medium">Typical Numbers</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Monthly Cost (Healthy 30yr, $500K)", value: "$200 – $500", color: "text-zinc-200" },
            { label: "Avg Annual Return (Historical)", value: "5% – 8%", color: "text-violet-400" },
            { label: "Years to Build Meaningful Cash Value", value: "7 – 15 years", color: "text-amber-400" },
            { label: "Coverage Duration", value: "Lifetime", color: "text-emerald-400" },
          ].map((stat, i) => (
            <div key={i}>
              <p className="text-[9px] uppercase tracking-wider text-zinc-600 mb-1">{stat.label}</p>
              <p className={`text-sm font-data font-bold ${stat.color}`}>{stat.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Who it's for */}
      <div className="bg-zinc-900/30 border border-zinc-800/50 rounded-sm p-5">
        <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3 font-medium">Who Should Consider an IUL?</h3>
        <ul className="space-y-2">
          {[
            "People who want permanent coverage with higher growth potential than whole life.",
            "High-income earners looking for tax-free retirement income through policy loans.",
            "People who want market-linked growth but can't stomach actual market losses.",
            "Those with a long time horizon (20+ years) to let the cash value compound.",
            "Business owners using it for supplemental retirement planning or key-person insurance.",
            "Anyone disciplined enough to consistently fund the policy — underfunding an IUL is the #1 way it fails.",
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2">
              <CheckCircle size={10} weight="bold" className="text-zinc-500 flex-shrink-0 mt-0.5" />
              <span className="text-xs text-zinc-400 leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Comparison callout */}
      <div className="bg-violet-900/10 border border-violet-800/30 rounded-sm p-5">
        <h3 className="text-xs font-medium text-violet-300 mb-2 flex items-center gap-2">
          <Scales size={14} weight="bold" /> IUL vs. Whole Life — Quick Comparison
        </h3>
        <div className="grid grid-cols-3 gap-3 text-[10px]">
          <div className="font-medium text-zinc-400"></div>
          <div className="font-medium text-blue-400 text-center">Whole Life</div>
          <div className="font-medium text-violet-400 text-center">IUL</div>

          <div className="text-zinc-500">Growth Rate</div>
          <div className="text-zinc-300 text-center">1-3% guaranteed</div>
          <div className="text-zinc-300 text-center">0-13% (market-linked)</div>

          <div className="text-zinc-500">Premiums</div>
          <div className="text-zinc-300 text-center">Fixed forever</div>
          <div className="text-zinc-300 text-center">Flexible</div>

          <div className="text-zinc-500">Risk</div>
          <div className="text-zinc-300 text-center">None</div>
          <div className="text-zinc-300 text-center">Low (0% floor)</div>

          <div className="text-zinc-500">Complexity</div>
          <div className="text-zinc-300 text-center">Simple</div>
          <div className="text-zinc-300 text-center">Complex</div>

          <div className="text-zinc-500">Best For</div>
          <div className="text-zinc-300 text-center">Certainty seekers</div>
          <div className="text-zinc-300 text-center">Growth seekers</div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   INFINITE BANKING CONCEPT (IBC)
   ============================================================ */
function InfiniteBankingTab() {
  return (
    <div className="p-5 max-w-[1400px] space-y-6" data-testid="infinite-banking-tab">
      {/* Hero */}
      <div className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-sm bg-amber-900/20 flex items-center justify-center">
            <Bank size={20} weight="bold" className="text-amber-400" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-zinc-50">Infinite Banking Concept (IBC)</h2>
            <p className="text-xs text-zinc-400">Become your own bank using a dividend-paying whole life policy</p>
          </div>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed">
          Infinite Banking is a strategy where you use a specially-designed <strong className="text-zinc-100">dividend-paying whole life insurance policy</strong> as your own personal bank. Instead of keeping savings in a bank account (earning ~0.5%) or borrowing from a bank (paying 7-20% interest), you build cash value inside a life insurance policy, then borrow against that cash value to fund cars, homes, businesses, or investments — all while the full cash value keeps growing as if you never touched it.
        </p>
        <p className="text-xs text-zinc-500 leading-relaxed mt-3">
          The concept was popularized by R. Nelson Nash in his 2000 book <em>"Becoming Your Own Banker."</em> It's not a product — it's a <strong className="text-zinc-300">strategy</strong> built on top of specific whole life policies.
        </p>
      </div>

      {/* How it works */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm overflow-hidden">
        <div className="p-5 border-b border-zinc-800/50">
          <h3 className="text-sm font-heading font-bold text-zinc-100 flex items-center gap-2">
            <Repeat size={14} weight="bold" className="text-amber-400" /> How Infinite Banking Works
          </h3>
        </div>
        <div className="p-5 space-y-3">
          {[
            "You buy a dividend-paying whole life policy from a mutual insurance company (one owned by its policyholders, not shareholders). It's specifically designed with a Paid-Up Additions (PUA) rider to maximize cash value early.",
            "You overfund the policy — paying more than the minimum premium — so the cash value grows quickly. In the first few years, up to 60-80% of what you pay goes into usable cash value.",
            "After 2-5 years, you have meaningful cash value. Now you can take a policy loan from the insurance company, using your cash value as collateral.",
            "Here's the magic: when you take a loan, your full cash value keeps earning interest and dividends as if you never borrowed. You pay interest to the insurance company (5-8%), but your money keeps compounding at 4-6% underneath.",
            "You repay the loan on your own schedule — there's no fixed payment plan. Miss a payment? No credit bureau gets notified. You can't be denied or reported.",
            "When you repay the loan, that capital is available again to borrow. The cycle repeats — you become your own source of financing for life.",
            "When you die, your family receives the death benefit minus any outstanding loan balance. The cash value doesn't go to them separately — it's already been absorbed into the death benefit.",
          ].map((step, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-amber-900/30 text-amber-400 flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i + 1}</span>
              <p className="text-xs text-zinc-300 leading-relaxed">{step}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Real example */}
      <div className="bg-zinc-900/40 border border-amber-800/30 rounded-sm p-5">
        <h3 className="text-xs font-medium text-amber-300 mb-3 flex items-center gap-2">
          <CurrencyDollar size={14} weight="bold" /> Simple Example
        </h3>
        <div className="space-y-3 text-xs text-zinc-300 leading-relaxed">
          <p>
            <strong className="text-zinc-100">You need $30,000 to buy a car.</strong> Traditional options:
          </p>
          <ul className="space-y-1.5 pl-4">
            <li>• <strong className="text-red-400">Bank loan (7% for 5 years):</strong> Pay ~$35,600 total. Your $30K savings earns 0.5% while shrinking as you pay the loan.</li>
            <li>• <strong className="text-red-400">Pay cash from savings:</strong> You lose the compounding on that $30K forever.</li>
          </ul>
          <p className="pt-2">
            <strong className="text-zinc-100">Infinite Banking option:</strong>
          </p>
          <ul className="space-y-1.5 pl-4">
            <li>• You borrow $30K from your policy at 5% interest.</li>
            <li>• Your FULL $30K cash value keeps earning ~5% dividends and growing.</li>
            <li>• You pay the insurance company back on your own schedule — could be $600/month, could be a lump sum when you get a bonus.</li>
            <li>• Net cost: the spread between what you pay (5%) and what your money earns (5%) = roughly zero, minus modest policy costs.</li>
            <li>• When the loan is repaid, the $30K is available again — to buy the next car, fund a business, or grab an investment opportunity.</li>
          </ul>
          <p className="text-[11px] text-zinc-400 italic pt-2">
            This is the "uninterrupted compounding" that makes IBC powerful over decades.
          </p>
        </div>
      </div>

      {/* Pros and Cons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-zinc-900/40 border border-emerald-900/30 rounded-sm p-5">
          <h3 className="text-[10px] uppercase tracking-wider text-emerald-400 mb-3 font-medium flex items-center gap-1.5">
            <CheckCircle size={12} weight="bold" /> Advantages
          </h3>
          <ul className="space-y-2.5">
            {[
              "Uninterrupted compounding — Your cash value keeps earning even when you borrow against it. Traditional savings stop growing the moment you withdraw.",
              "Bypass the banking system — No credit checks, no loan applications, no approval delays. The cash value is collateral, so loans are automatic.",
              "Tax-free access to capital — Policy loans aren't taxable income. You can pull money out without triggering IRS events.",
              "Flexible repayment — No fixed payment schedule. Repay when cash flow allows. Missing a payment doesn't hurt your credit.",
              "Permanent death benefit — Your family is protected for life. If you die with an outstanding loan, it's just deducted from the death benefit.",
              "Creditor protection — In most states, cash value in a life insurance policy is protected from creditors, lawsuits, and bankruptcy.",
              "Generational wealth tool — The death benefit passes income-tax-free to heirs. Structured right, it's a vehicle for multi-generational wealth.",
            ].map((pro, i) => (
              <li key={i} className="flex items-start gap-2">
                <ArrowRight size={10} weight="bold" className="text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-xs text-zinc-300 leading-relaxed">{pro}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-zinc-900/40 border border-red-900/30 rounded-sm p-5">
          <h3 className="text-[10px] uppercase tracking-wider text-red-400 mb-3 font-medium flex items-center gap-1.5">
            <WarningCircle size={12} weight="bold" /> Disadvantages
          </h3>
          <ul className="space-y-2.5">
            {[
              "Long setup time — It takes 3-7 years before the policy has enough cash value to be useful. Not a quick-start system.",
              "Requires discipline — You have to actually repay the loans. Otherwise, the policy can collapse or your death benefit shrinks significantly.",
              "High commitment — Premiums are typically $500-$5,000+/month and must be paid consistently for decades. Not flexible like a Roth IRA.",
              "Slower growth than markets — A well-designed policy returns 4-6%. An S&P 500 index fund historically averages ~10%. You're trading growth for access + certainty.",
              "Commissions can be high — First-year commissions for agents can eat up 50%+ of premiums. A properly structured IBC policy pays LESS commission, but many agents won't sell them.",
              "Complexity — Finding an agent who understands IBC is hard. Most agents sell standard whole life that is NOT optimized for banking.",
              "MEC risk — If you overfund too aggressively, the IRS may classify it as a Modified Endowment Contract (MEC), which kills the tax advantages.",
            ].map((con, i) => (
              <li key={i} className="flex items-start gap-2">
                <ArrowRight size={10} weight="bold" className="text-red-400 flex-shrink-0 mt-0.5" />
                <span className="text-xs text-zinc-300 leading-relaxed">{con}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Key numbers */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-5">
        <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3 font-medium">Typical Numbers</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Typical Monthly Premium", value: "$500 – $5,000+", color: "text-zinc-200" },
            { label: "Policy Loan Interest", value: "5% – 8%", color: "text-amber-400" },
            { label: "Cash Value Growth (Guaranteed + Dividend)", value: "4% – 6%", color: "text-emerald-400" },
            { label: "Years to Break Even", value: "5 – 10 years", color: "text-blue-400" },
          ].map((stat, i) => (
            <div key={i}>
              <p className="text-[9px] uppercase tracking-wider text-zinc-600 mb-1">{stat.label}</p>
              <p className={`text-sm font-data font-bold ${stat.color}`}>{stat.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Key requirements */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-5">
        <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3 font-medium">What Makes a Policy "IBC-Ready"</h3>
        <ul className="space-y-2.5">
          {[
            { key: "Mutual company", val: "Buy from a mutual insurer (owned by policyholders) so you receive dividends. Avoid stock companies." },
            { key: "Dividend-paying whole life", val: "Must be participating whole life — not term, not universal, not IUL. Dividends are what make this strategy work long-term." },
            { key: "PUA rider (critical)", val: "Paid-Up Additions rider lets you overfund the policy to build cash value fast. Without this, it can take 15+ years to break even." },
            { key: "Non-direct recognition loans", val: "Some insurers reduce dividends on the borrowed portion (direct recognition). Non-direct companies pay dividends on the full cash value even while you have a loan — ideal for banking." },
            { key: "Stay under MEC limits", val: "There's a federal cap on how fast you can overfund. An agent who specializes in IBC will structure the policy to max cash value WITHOUT triggering MEC status." },
          ].map((req, i) => (
            <li key={i} className="flex items-start gap-3">
              <Lock size={12} weight="bold" className="text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <span className="text-xs font-medium text-zinc-200">{req.key}</span>
                <p className="text-[11px] text-zinc-400 leading-relaxed mt-0.5">{req.val}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Who it's for */}
      <div className="bg-zinc-900/30 border border-zinc-800/50 rounded-sm p-5">
        <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3 font-medium">Who Should Consider Infinite Banking?</h3>
        <ul className="space-y-2">
          {[
            "People with strong, stable cash flow who can comfortably commit $500-$5,000+/month for decades without stress.",
            "Business owners and real estate investors who frequently need access to capital for opportunities.",
            "High-income earners who have already maxed out Roth IRA, 401(k), and HSA — looking for another tax-sheltered bucket.",
            "Disciplined savers who consistently pay back what they borrow. If you can't resist spending without repayment, IBC will fail for you.",
            "Long-term thinkers with a 20+ year horizon. The system shines over decades, not years.",
            "Those who value control, privacy, and flexibility over maximizing raw returns.",
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2">
              <CheckCircle size={10} weight="bold" className="text-zinc-500 flex-shrink-0 mt-0.5" />
              <span className="text-xs text-zinc-400 leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Bottom line */}
      <div className="bg-amber-900/10 border border-amber-800/30 rounded-sm p-5" data-testid="ibc-bottom-line">
        <h3 className="text-xs font-medium text-amber-300 mb-3 flex items-center gap-2">
          <CurrencyDollar size={14} weight="bold" /> The Bottom Line
        </h3>
        <div className="space-y-3">
          <p className="text-xs text-zinc-300 leading-relaxed">
            <strong className="text-zinc-100">Infinite Banking is a cash flow management system</strong>, not an investment strategy. The goal isn't to beat the stock market — it's to control the flow of your money so compounding never stops, and you always have access to capital on your own terms.
          </p>
          <p className="text-xs text-zinc-300 leading-relaxed">
            <strong className="text-zinc-100">It's not for everyone.</strong> If you're still building your emergency fund, maxing your Roth IRA, or don't have stable income, this is premature. Start there first. IBC works best as a <em>layer</em> on top of a healthy financial foundation.
          </p>
          <p className="text-xs text-zinc-300 leading-relaxed">
            <strong className="text-zinc-100">Find a specialist.</strong> Never buy an IBC policy from a generalist life insurance agent. Look for agents certified by the Nelson Nash Institute (NNI) or with 10+ years of IBC-specific experience. A poorly designed policy can take decades to become useful, while a well-designed one can work for you in as little as 3-5 years.
          </p>
          <p className="text-[11px] text-zinc-500 italic pt-1">
            This is educational content, not financial advice. Consult a licensed insurance professional and a fee-only financial advisor before committing to any whole life policy.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   ROTH IRA TAB
   ============================================================ */
function RothTab() {
  return (
    <div className="p-5 max-w-[1400px] space-y-6" data-testid="roth-tab">
      {/* Hero */}
      <div className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-sm bg-emerald-900/20 flex items-center justify-center">
            <Vault size={20} weight="bold" className="text-emerald-400" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-zinc-50">Roth IRA</h2>
            <p className="text-xs text-zinc-400">Tax-free retirement account — you invest after-tax dollars and never pay taxes on the growth</p>
          </div>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed">
          A Roth IRA is a retirement investment account where you contribute money you've already paid taxes on. The money then grows tax-free, and when you withdraw it in retirement (after age 59 and a half), you pay zero taxes on the gains. Unlike life insurance policies, a Roth IRA is a pure investment vehicle — no death benefit, no insurance costs, just your money growing in the market.
        </p>
      </div>

      {/* How it works */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm overflow-hidden">
        <div className="p-5 border-b border-zinc-800/50">
          <h3 className="text-sm font-heading font-bold text-zinc-100 flex items-center gap-2">
            <Clock size={14} weight="bold" className="text-emerald-400" /> How a Roth IRA Works
          </h3>
        </div>
        <div className="p-5 space-y-3">
          {[
            "You contribute after-tax money (up to $7,000/year in 2024-2025, $8,000 if you're 50+). This means you've already paid income tax on this money.",
            "You choose what to invest in — stocks, index funds, ETFs, bonds, or even crypto on some platforms. You control the investments, not an insurance company.",
            "Your investments grow tax-free. No capital gains tax, no dividend tax, nothing — as long as the money stays in the account.",
            "After age 59 and a half (and the account has been open 5+ years), you withdraw everything tax-free. The growth, the dividends, all of it — $0 in taxes.",
            "You can withdraw your contributions (not gains) at any time without penalty. This makes it more flexible than a 401(k) or life insurance cash value.",
            "There are income limits — if you earn too much (over ~$161K single / ~$240K married in 2024), you can't contribute directly. But there's a workaround called a 'Backdoor Roth.'",
          ].map((step, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-emerald-900/30 text-emerald-400 flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i + 1}</span>
              <p className="text-xs text-zinc-300 leading-relaxed">{step}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Key numbers */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-5">
        <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3 font-medium">Key Numbers</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Annual Contribution Limit", value: "$7,000", color: "text-emerald-400" },
            { label: "Catch-Up (Age 50+)", value: "$8,000", color: "text-blue-400" },
            { label: "Avg S&P 500 Return (Historical)", value: "~10%/yr", color: "text-violet-400" },
            { label: "Tax on Withdrawals (Qualified)", value: "$0", color: "text-emerald-400" },
          ].map((stat, i) => (
            <div key={i}>
              <p className="text-[9px] uppercase tracking-wider text-zinc-600 mb-1">{stat.label}</p>
              <p className={`text-sm font-data font-bold ${stat.color}`}>{stat.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Pros and Cons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-zinc-900/40 border border-emerald-900/30 rounded-sm p-5">
          <h3 className="text-[10px] uppercase tracking-wider text-emerald-400 mb-3 font-medium flex items-center gap-1.5">
            <CheckCircle size={12} weight="bold" /> Advantages
          </h3>
          <ul className="space-y-2.5">
            {[
              "Tax-free growth and withdrawals — The biggest advantage. You never pay taxes on gains.",
              "Full market returns — No caps, no floors, no participation rates. If the S&P 500 returns 25%, you earn 25%.",
              "Low fees — Index funds charge 0.03-0.10% per year. Compare that to 2-3% in life insurance products.",
              "Flexibility — Withdraw your contributions anytime without penalty (gains must wait until 59 and a half).",
              "No required minimum distributions (RMDs) — Unlike a Traditional IRA or 401(k), you're never forced to withdraw.",
              "Simple and transparent — You know exactly what you own and what the fees are. No complex illustrations.",
            ].map((pro, i) => (
              <li key={i} className="flex items-start gap-2">
                <ArrowRight size={10} weight="bold" className="text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-xs text-zinc-300 leading-relaxed">{pro}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-zinc-900/40 border border-red-900/30 rounded-sm p-5">
          <h3 className="text-[10px] uppercase tracking-wider text-red-400 mb-3 font-medium flex items-center gap-1.5">
            <WarningCircle size={12} weight="bold" /> Disadvantages
          </h3>
          <ul className="space-y-2.5">
            {[
              "Contribution limits are low — $7,000/year is not much compared to what you can put into life insurance.",
              "Income limits — High earners can't contribute directly (must use Backdoor Roth strategy).",
              "No death benefit — Your family gets the account balance, not a guaranteed payout. No life insurance protection.",
              "Market risk — Your investments can lose value. There's no floor protecting you from losses.",
              "No tax deduction — You pay taxes now, not later. If your tax rate drops in retirement, this could be less efficient than a Traditional IRA.",
              "Penalty on early gains withdrawal — Withdrawing investment gains before 59 and a half triggers taxes + a 10% penalty.",
            ].map((con, i) => (
              <li key={i} className="flex items-start gap-2">
                <ArrowRight size={10} weight="bold" className="text-red-400 flex-shrink-0 mt-0.5" />
                <span className="text-xs text-zinc-300 leading-relaxed">{con}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* The bottom line */}
      <div className="bg-emerald-900/10 border border-emerald-800/30 rounded-sm p-5" data-testid="roth-bottom-line">
        <h3 className="text-xs font-medium text-emerald-300 mb-3 flex items-center gap-2">
          <CurrencyDollar size={14} weight="bold" /> The Bottom Line
        </h3>
        <div className="space-y-3">
          <p className="text-xs text-zinc-300 leading-relaxed">
            <strong className="text-zinc-100">For most people, max out your Roth IRA first.</strong> The combination of tax-free growth, full market returns, low fees, and flexibility makes it the most efficient wealth-building tool available. At $7,000/year invested in an S&P 500 index fund averaging 10% returns, you'd have roughly $440,000 after 20 years and over $1.2 million after 30 years — all tax-free.
          </p>
          <p className="text-xs text-zinc-300 leading-relaxed">
            <strong className="text-zinc-100">Life insurance products make sense in specific situations:</strong> If you've already maxed your Roth IRA and 401(k), need a death benefit for your family, want to fund estate planning, or are a high earner looking for additional tax-sheltered income. An IUL can complement a Roth, but should rarely replace it.
          </p>
          <p className="text-xs text-zinc-300 leading-relaxed">
            <strong className="text-zinc-100">The priority order for most people:</strong> (1) Employer 401(k) match (free money), (2) Max Roth IRA, (3) Max 401(k), (4) Then consider life insurance products if you still have money to invest and need the death benefit.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   COMPARISON TAB
   ============================================================ */
function ComparisonTab() {
  return (
    <div className="p-5 max-w-[1400px] space-y-6" data-testid="comparison-tab">
      {/* Hero */}
      <div className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-sm bg-amber-900/20 flex items-center justify-center">
            <Scales size={20} weight="bold" className="text-amber-400" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-zinc-50">Roth IRA vs. Whole Life vs. IUL</h2>
            <p className="text-xs text-zinc-400">Side-by-side comparison to help you understand the tradeoffs</p>
          </div>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed">
          Each of these vehicles serves a different purpose. A Roth IRA is a pure investment tool with the best tax-free growth. Whole Life provides guaranteed protection and conservative cash value. An IUL bridges the gap with market-linked growth plus a death benefit. The right choice depends on your income, goals, and what you've already maxed out.
        </p>
      </div>

      {/* Main Comparison Table */}
      <div className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm overflow-hidden" data-testid="full-comparison">
        <div className="overflow-x-auto">
          <table className="w-full text-[10px]" data-testid="comparison-table">
            <thead>
              <tr className="border-b border-zinc-800/60 bg-zinc-900/60">
                <th className="text-left px-4 py-3 text-zinc-500 font-medium uppercase tracking-wider w-[180px]">Feature</th>
                <th className="text-center px-4 py-3 text-emerald-400 font-medium uppercase tracking-wider">Roth IRA</th>
                <th className="text-center px-4 py-3 text-blue-400 font-medium uppercase tracking-wider">Whole Life</th>
                <th className="text-center px-4 py-3 text-violet-400 font-medium uppercase tracking-wider">IUL</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: "Purpose", roth: "Retirement investing", whole: "Insurance + savings", iul: "Insurance + market-linked growth" },
                { feature: "Death Benefit", roth: "None", whole: "Guaranteed", iul: "Guaranteed (adjustable)" },
                { feature: "Growth Rate", roth: "Full market returns (~10%/yr avg)", whole: "1-3% guaranteed", iul: "0-13% (capped, market-linked)" },
                { feature: "Downside Risk", roth: "Yes — can lose money", whole: "None — guaranteed", iul: "Protected (0% floor)" },
                { feature: "Annual Cost / Fees", roth: "0.03-0.10% (index funds)", whole: "High (built into premium)", iul: "2-3% (insurance + admin fees)" },
                { feature: "Tax on Growth", roth: "Tax-free", whole: "Tax-deferred", iul: "Tax-deferred" },
                { feature: "Tax on Withdrawal", roth: "Tax-free (after 59.5)", whole: "Loans tax-free; withdrawals taxed", iul: "Loans tax-free if structured right" },
                { feature: "Contribution Limit", roth: "$7,000/yr ($8,000 if 50+)", whole: "No limit (premium-based)", iul: "No limit (premium-based)" },
                { feature: "Flexibility", roth: "High — change investments anytime", whole: "Low — fixed premiums", iul: "Medium — flexible premiums" },
                { feature: "Complexity", roth: "Very simple", whole: "Simple", iul: "Complex" },
                { feature: "Best 20-Year Outcome", roth: "$7K/yr at 10% = ~$440K", whole: "$7K/yr at 2% = ~$175K (plus death benefit)", iul: "$7K/yr at 6% = ~$275K (plus death benefit)" },
                { feature: "Worst-Case Scenario", roth: "Market crash — account drops", whole: "Overpaid for low returns", iul: "Underfunded — policy lapses" },
                { feature: "Best For", roth: "Most people (max this first)", whole: "Estate planning, certainty seekers", iul: "High earners needing tax-free income + coverage" },
              ].map((row, i) => (
                <tr key={i} className="border-b border-zinc-800/30 hover:bg-zinc-900/30">
                  <td className="px-4 py-2.5 text-zinc-400 font-medium">{row.feature}</td>
                  <td className="px-4 py-2.5 text-zinc-300 text-center">{row.roth}</td>
                  <td className="px-4 py-2.5 text-zinc-300 text-center">{row.whole}</td>
                  <td className="px-4 py-2.5 text-zinc-300 text-center">{row.iul}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* When to use which */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-zinc-900/40 border border-emerald-900/30 rounded-sm p-5">
          <h3 className="text-xs font-medium text-emerald-400 mb-3 flex items-center gap-1.5">
            <CheckCircle size={12} weight="bold" /> Choose Roth IRA If...
          </h3>
          <ul className="space-y-2">
            {[
              "You haven't maxed your Roth IRA yet — do this FIRST before considering any life insurance product.",
              "You want the highest possible long-term returns with the lowest fees.",
              "You're comfortable with market volatility and have 10+ years until retirement.",
              "You want full control over your investments — choose your own stocks, index funds, or ETFs.",
              "You don't need a death benefit or already have separate term life insurance.",
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <ArrowRight size={9} weight="bold" className="text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-[10px] text-zinc-400 leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-zinc-900/40 border border-blue-900/30 rounded-sm p-5">
          <h3 className="text-xs font-medium text-blue-400 mb-3 flex items-center gap-1.5">
            <ShieldCheck size={12} weight="bold" /> Choose Whole Life If...
          </h3>
          <ul className="space-y-2">
            {[
              "You want guaranteed cash value growth — no market risk, no chance of losing money.",
              "You need a permanent death benefit for estate planning or to leave a guaranteed legacy.",
              "You want to use the Infinite Banking Concept — borrow against your policy as your own personal bank.",
              "You've already maxed all tax-advantaged accounts (Roth, 401k) and want additional sheltered savings.",
              "You value certainty and simplicity over maximizing returns.",
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <ArrowRight size={9} weight="bold" className="text-blue-400 flex-shrink-0 mt-0.5" />
                <span className="text-[10px] text-zinc-400 leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-zinc-900/40 border border-violet-900/30 rounded-sm p-5">
          <h3 className="text-xs font-medium text-violet-400 mb-3 flex items-center gap-1.5">
            <TrendUp size={12} weight="bold" /> Choose IUL If...
          </h3>
          <ul className="space-y-2">
            {[
              "You're a high earner who has maxed out Roth IRA and 401(k) and needs additional tax-sheltered growth.",
              "You want market-linked returns with downside protection (0% floor in down years).",
              "You plan to use tax-free policy loans as retirement income on top of your Roth withdrawals.",
              "You need a death benefit AND want the cash value to grow faster than Whole Life.",
              "You can commit to funding the policy properly for 15-20+ years — underfunding kills IUL policies.",
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <ArrowRight size={9} weight="bold" className="text-violet-400 flex-shrink-0 mt-0.5" />
                <span className="text-[10px] text-zinc-400 leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Bottom line */}
      <div className="bg-amber-900/10 border border-amber-800/30 rounded-sm p-5" data-testid="comparison-bottom-line">
        <h3 className="text-xs font-medium text-amber-300 mb-3 flex items-center gap-2">
          <CurrencyDollar size={14} weight="bold" /> The Priority Order
        </h3>
        <div className="space-y-3">
          <p className="text-xs text-zinc-300 leading-relaxed">
            <strong className="text-zinc-100">For most people, the optimal order is:</strong> (1) Get your employer 401(k) match (free money), (2) Max your Roth IRA ($7K/year), (3) Max your 401(k) ($23K/year), (4) Then consider Whole Life or IUL if you still have money to deploy and need the death benefit + tax-free policy loans.
          </p>
          <p className="text-xs text-zinc-300 leading-relaxed">
            <strong className="text-zinc-100">They're not mutually exclusive.</strong> The wealthiest people use ALL of these. A Roth IRA for tax-free market growth. A Whole Life policy for guaranteed legacy and Infinite Banking. An IUL for additional tax-free retirement income. The key is filling them in the right order based on your income and where you are in your financial journey.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   MAIN PAGE — Tabbed Layout
   ============================================================ */
const SUB_TABS = [
  { key: "whole-life", label: "Whole Life", icon: ShieldCheck },
  { key: "iul", label: "IULs", icon: TrendUp },
  { key: "infinite-banking", label: "Infinite Banking", icon: Bank },
  { key: "roth", label: "Roth IRA", icon: Vault },
  { key: "401k", label: "401(k)", icon: Briefcase },
  { key: "tsp", label: "TSP", icon: Flag },
  { key: "index-funds", label: "Index Funds", icon: ChartLine },
  { key: "comparison", label: "Comparison", icon: Scales },
];

export default function LifeInsurance() {
  const [tab, setTab] = useState("whole-life");

  // Switching sub-tabs scrolls main content back to top so user starts
  // at the new sub-tab's hero instead of mid-scroll where the last one ended.
  const onTabChange = (next) => {
    setTab(next);
    const main = document.querySelector('[data-testid="main-content"]');
    if (main) main.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  };

  return (
    <div data-testid="life-insurance-page">
      <div
        className="sticky z-20 bg-zinc-950 border-b border-zinc-800/60 px-3 md:px-5 pt-3 md:pt-4 pb-0"
        style={{ top: "var(--fe-tabs-h, 108px)", transform: "translateZ(0)" }}
      >
        <div className="flex items-center justify-between mb-2 md:mb-3">
          <div className="flex items-center gap-2 md:gap-2.5 min-w-0">
            <Heart size={20} weight="bold" className="text-rose-400 shrink-0" />
            <h1 className="font-heading text-lg md:text-2xl font-bold text-zinc-50 tracking-tight">Retirement Vehicles</h1>
            <Badge className="text-[8px] px-1.5 py-0.5 border-0 bg-rose-900/30 text-rose-400 h-4">EDUCATION</Badge>
          </div>
        </div>
        <Tabs value={tab} onValueChange={onTabChange}>
          <TabsList
            className="h-9 bg-zinc-900/60 border border-zinc-800/60 p-0.5 gap-0.5 overflow-x-auto scrollbar-hide max-w-full flex justify-start"
            style={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" }}
            data-testid="insurance-tabs"
          >
            {SUB_TABS.map(t => {
              const Icon = t.icon;
              return (
                <TabsTrigger key={t.key} value={t.key} data-testid={`insurance-tab-${t.key}`}
                  className="text-[11px] px-3 py-1.5 h-8 gap-1.5 font-medium transition-colors whitespace-nowrap shrink-0 data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-50 data-[state=active]:shadow-sm">
                  <Icon size={13} weight="bold" />
                  {t.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
      </div>

      <div data-testid={`insurance-content-${tab}`}>
        {tab === "whole-life" && <WholeLifeTab />}
        {tab === "iul" && <IulTab />}
        {tab === "infinite-banking" && <InfiniteBankingTab />}
        {tab === "roth" && <RothTab />}
        {tab === "401k" && <Retire401k />}
        {tab === "tsp" && <RetireTSP />}
        {tab === "index-funds" && <RetireIndexFunds />}
        {tab === "comparison" && (
          <>
            <RetireCompare />
            <ComparisonTab />
          </>
        )}
      </div>
    </div>
  );
}
